import React from 'react';
import { useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

const MOBILE_QUERY = '(max-width:599.95px)';

function rowMissing(missingRows, row) {
  if (!missingRows) return false;
  if (typeof missingRows.has === 'function') return missingRows.has(row);
  return Array.isArray(missingRows) && missingRows.includes(row);
}

export default function MatrixQuestion({
  rows = [],
  columns = [],
  value,
  onChange,
  missingRows,
  disabled = false,
  readOnly = false
}) {
  const mobile = useMediaQuery(MOBILE_QUERY, { noSsr: true });
  const answers = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const choose = (row, col) => {
    if (disabled || readOnly || typeof onChange !== 'function') return;
    onChange({ ...answers, [row]: col });
  };

  if (mobile) {
    return (
      <Box data-matrix-layout="stack">
        {!readOnly && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Выберите значение в каждой строке
          </Typography>
        )}
        {rows.map((row, rowIdx) => {
          const missing = rowMissing(missingRows, row);
          return (
            <Box
              key={`${rowIdx}-${row}`}
              sx={{ mb: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 0.5, color: missing ? '#d63031' : 'inherit', fontWeight: 700 }}
              >
                {row}
              </Typography>
              {readOnly ? (
                columns.map((col, colIdx) => (
                  <Typography key={`${colIdx}-${col}`} variant="body2" sx={{ py: 0.25, pl: 0.5 }}>
                    {col}
                  </Typography>
                ))
              ) : (
                <RadioGroup
                  value={answers[row] || ''}
                  onChange={(event) => choose(row, event.target.value)}
                >
                  {columns.map((col, colIdx) => (
                    <FormControlLabel
                      key={`${colIdx}-${col}`}
                      value={col}
                      disabled={disabled}
                      control={<Radio size="small" />}
                      label={col}
                      sx={{ alignItems: 'flex-start', mx: 0, py: 0.25 }}
                    />
                  ))}
                </RadioGroup>
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto' }} data-matrix-layout="table">
      {!readOnly && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Выберите значение в каждой строке
        </Typography>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              {columns.map((col, idx) => (
                <TableCell key={idx} align="center" sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="caption">{col}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, rowIdx) => {
              const missing = rowMissing(missingRows, row);
              return (
                <TableRow key={`${rowIdx}-${row}`}>
                  <TableCell sx={{ fontWeight: 500, minWidth: 140 }}>
                    <Typography
                      variant="body2"
                      sx={missing ? { color: '#d63031', fontWeight: 700 } : undefined}
                    >
                      {row}
                    </Typography>
                  </TableCell>
                  {columns.map((col, colIdx) => (
                    <TableCell key={colIdx} align="center">
                      {readOnly ? '—' : (
                        <Radio
                          size="small"
                          disabled={disabled}
                          checked={answers[row] === col}
                          onChange={() => choose(row, col)}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
